/**
 * Desktop OS detection for the app download menu.
 *
 * Mirrors `getPlatform()` in jitsi-meet, with two deliberate differences: the
 * user agent is preferred over the deprecated `navigator.platform`, and mobile
 * devices resolve to `null` so an iPad — which reports `MacIntel` — is never
 * offered the macOS desktop build.
 */

import { detectMobilePlatform } from './mobilePlatform';

export type DesktopPlatform = 'windows' | 'macos' | 'linux';

const WINDOWS_REGEX = /Windows|Win32|Win64/i;
const MACOS_REGEX = /Mac OS X|Macintosh|MacIntel/i;
const LINUX_REGEX = /Linux|X11|CrOS/i;

export function detectDesktopPlatform(): DesktopPlatform | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  // Android reports "Linux" and iPadOS reports "Macintosh", so mobile has to be
  // ruled out before any of the desktop patterns are tried.
  if (detectMobilePlatform() !== null) {
    return null;
  }

  const signature = `${navigator.userAgent ?? ''} ${navigator.platform ?? ''}`;

  if (WINDOWS_REGEX.test(signature)) {
    return 'windows';
  }

  if (MACOS_REGEX.test(signature)) {
    return 'macos';
  }

  if (LINUX_REGEX.test(signature)) {
    return 'linux';
  }

  return null;
}
